/**
 * Per-page Meta Conversions API selector.
 *
 * Replaces the bare MetaCapiSettings (mode="page") form on the Builder
 * with a UX that surfaces the new account-level multi-token feature:
 *
 *   1. Inheriting (default)         — page uses the account's default
 *                                     token. Nothing persisted on the
 *                                     page row.
 *   2. Pick from account pool       — page sets `tokenId` pointing at
 *                                     a specific account-level token.
 *   3. Custom override              — page provides its own access
 *                                     token + (optional) test code.
 *                                     Useful when one landing page
 *                                     needs a token that the operator
 *                                     doesn't want to keep account-wide.
 *
 * The "Custom override" path reuses MetaCapiSettings (mode="page") so
 * the existing token-paste UX, masking, and "Send test event" all
 * keep working without duplication.
 */

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Activity, AlertCircle, CheckCircle2, Globe, KeyRound, Pencil } from 'lucide-react';
import { MetaCapiSettings } from './MetaCapiSettings';
import { ToggleSwitch } from './ToggleSwitch';

export interface PageCapiSelection {
  // Multi-select: page fires CAPI to EACH selected token in parallel.
  // When a token has its own pixelId pinned, only that pixel receives
  // events from that token (server-side enforcement). When the token
  // has no pinned pixelId, it broadcasts across page.facebookPixelIds.
  tokenIds?: string[];
  // Legacy single-select — kept for back-compat reads. New writes use
  // tokenIds[].
  tokenId?: string;
  accessToken?: string;
  testEventCode?: string;
  enabled?: boolean;
}

interface AccountTokenSummary {
  id: string;
  name: string;
  pixelId: string;
}

interface Props {
  value: PageCapiSelection;
  onChange: (next: PageCapiSelection) => void;
  accountTokens: AccountTokenSummary[];
  accountDefaultTokenId: string | null;
  defaultPixelId?: string;
  /**
   * When the selector is rendered inside a wrapper that already
   * provides its own title row and master enable toggle (e.g. the
   * Builder's collapsible TrackingSection), pass `compact` to suppress
   * the duplicate header and the inner per-page enable switch.
   */
  compact?: boolean;
}

type Mode = 'inherit' | 'pick' | 'custom';

function detectMode(value: PageCapiSelection): Mode {
  if (value.accessToken && value.accessToken.trim()) return 'custom';
  if (Array.isArray(value.tokenIds) && value.tokenIds.length) return 'pick';
  if (value.tokenId && value.tokenId.trim()) return 'pick';
  return 'inherit';
}

/** Resolve the effective list of selected token ids, handling legacy
 *  single-select rows that still carry the old `tokenId` field. */
function selectedTokenIdSet(value: PageCapiSelection): Set<string> {
  const out = new Set<string>();
  if (Array.isArray(value.tokenIds)) {
    for (const id of value.tokenIds) if (id) out.add(String(id));
  }
  if (value.tokenId) out.add(String(value.tokenId));
  return out;
}

export const PageMetaCapiSelector: React.FC<Props> = ({
  value,
  onChange,
  accountTokens,
  accountDefaultTokenId,
  defaultPixelId,
  compact = false,
}) => {
  const { t } = useTranslation('builder');
  const [mode, setMode] = React.useState<Mode>(() => detectMode(value));

  // When the parent's value changes (page swap), re-derive mode so
  // the radio reflects what's actually persisted.
  React.useEffect(() => {
    setMode(detectMode(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const hasAccountPool = accountTokens.length > 0;
  const accountDefault =
    accountTokens.find((t) => t.id === accountDefaultTokenId) ||
    accountTokens[0] ||
    null;

  function setModeAndClear(next: Mode) {
    setMode(next);
    if (next === 'inherit') {
      // Clear page-level overrides entirely so the account default
      // takes over on the next save. Keep `enabled` since the user
      // may have explicitly disabled CAPI for this page.
      onChange({ enabled: value.enabled });
    } else if (next === 'pick') {
      // Pre-seed with the existing selection (legacy tokenId or new
      // tokenIds[]). If neither, default to the account default so
      // the operator doesn't see an empty checkbox list.
      const existing = selectedTokenIdSet(value);
      const seed = existing.size
        ? Array.from(existing)
        : accountDefault
          ? [accountDefault.id]
          : [];
      onChange({
        ...value,
        tokenIds: seed,
        tokenId: undefined,
        accessToken: undefined,
      });
    } else {
      // custom — drop the picked ids, keep any pasted token if present.
      onChange({
        ...value,
        tokenId: undefined,
        tokenIds: undefined,
      });
    }
  }

  function toggleToken(id: string) {
    const current = selectedTokenIdSet(value);
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    onChange({
      ...value,
      tokenIds: Array.from(current),
      tokenId: undefined, // migrate away from legacy single-select
      accessToken: undefined,
    });
  }

  return (
    <div className="space-y-4">
      {!compact && (
        <>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" />
            <h4 className="text-base font-semibold text-white">
              {t('capiPageSelector.title')}
            </h4>
            {(mode === 'pick' || mode === 'custom') && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-500/15 text-blue-300">
                <Pencil className="w-3 h-3" /> {t('capiPageSelector.overridden')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400">
            {t('capiPageSelector.intro')}
          </p>
        </>
      )}
      {/* ── Mode selector ────────────────────────────────────────────
          Hidden in `compact` mode — the per-page UI deliberately exposes
          ONLY the pick-from-pool picker. Account defaults / custom
          token paste are configured globally on the Meta CAPI page. */}
      {!compact && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <ModeButton
            icon={<Globe className="w-4 h-4" />}
            title={t('capiPageSelector.mode.accountDefault.title')}
            subtitle={
              hasAccountPool && accountDefault
                ? t('capiPageSelector.mode.accountDefault.usesToken', { name: accountDefault.name })
                : t('capiPageSelector.mode.accountDefault.noTokensYet')
            }
            active={mode === 'inherit'}
            onClick={() => setModeAndClear('inherit')}
          />
          <ModeButton
            icon={<KeyRound className="w-4 h-4" />}
            title={t('capiPageSelector.mode.pickFromPool.title')}
            subtitle={(() => {
              if (!hasAccountPool) return t('capiPageSelector.mode.pickFromPool.addTokensFirst');
              const sel = selectedTokenIdSet(value).size;
              if (mode === 'pick' && sel > 0) {
                return t('capiPageSelector.mode.pickFromPool.selectedOf', { selected: sel, total: accountTokens.length });
              }
              return t('capiPageSelector.mode.pickFromPool.tokenAvailable', { count: accountTokens.length });
            })()}
            active={mode === 'pick'}
            disabled={!hasAccountPool}
            onClick={() => setModeAndClear('pick')}
          />
          <ModeButton
            icon={<Pencil className="w-4 h-4" />}
            title={t('capiPageSelector.mode.customOverride.title')}
            subtitle={t('capiPageSelector.mode.customOverride.subtitle')}
            active={mode === 'custom'}
            onClick={() => setModeAndClear('custom')}
          />
        </div>
      )}

      {/* ── Mode-specific body ────────────────────────────────────── */}
      {!compact && mode === 'inherit' && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-sm text-blue-200 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            {hasAccountPool && accountDefault ? (
              <>
                {t('capiPageSelector.inherit.prefix')}{' '}
                <span className="font-mono text-white">{accountDefault.name}</span>
                {accountDefault.pixelId && (
                  <> {t('capiPageSelector.inherit.pixelPart', { id: accountDefault.pixelId })}</>
                )}
                <Trans
                  i18nKey="capiPageSelector.inherit.suffix"
                  ns="builder"
                  components={{ meta: <span className="font-medium text-white" /> }}
                />
              </>
            ) : (
              <Trans
                i18nKey="capiPageSelector.inherit.noTokens"
                ns="builder"
                components={{ meta: <span className="font-medium text-white" /> }}
              />
            )}
          </div>
        </div>
      )}

      {(mode === 'pick' || compact) && !hasAccountPool && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 text-sm text-yellow-200 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <Trans
              i18nKey="capiPageSelector.noPoolYet"
              ns="builder"
              components={{ meta: <span className="font-medium text-white" /> }}
            />
          </div>
        </div>
      )}

      {(mode === 'pick' || compact) && hasAccountPool && (
        <div>
          {compact && selectedTokenIdSet(value).size === 0 && accountDefault && (
            <div className="mb-3 rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200 flex gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <Trans
                  i18nKey="capiPageSelector.compact.noneSelected"
                  ns="builder"
                  values={{ name: accountDefault.name }}
                  components={{ name: <span className="font-mono text-white" /> }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-300">
              {t('capiPageSelector.pickLabel')}
            </label>
            <div className="flex items-center gap-2 text-[11px]">
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    tokenIds: accountTokens.map((t) => t.id),
                    tokenId: undefined,
                    accessToken: undefined,
                  })
                }
                className="text-blue-400 hover:text-blue-300"
              >
                {t('capiPageSelector.selectAll')}
              </button>
              <span className="text-gray-600">·</span>
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    tokenIds: [],
                    tokenId: undefined,
                    accessToken: undefined,
                  })
                }
                className="text-gray-400 hover:text-gray-300"
              >
                {t('capiPageSelector.clear')}
              </button>
            </div>
          </div>
          {(() => {
            const selected = selectedTokenIdSet(value);
            return (
              <div className="space-y-2">
                {accountTokens.map((token) => {
                  const isOn = selected.has(token.id);
                  const isDefault = token.id === accountDefaultTokenId;
                  return (
                    <label
                      key={token.id}
                      className={
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ' +
                        (isOn
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-gray-700 bg-gray-800/60 hover:border-gray-600')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggleToken(token.id)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-white truncate">
                            {token.name}
                          </span>
                          {isDefault && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-medium uppercase tracking-wide">
                              {t('capiPageSelector.defaultBadge')}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          {token.pixelId ? (
                            <Trans
                              i18nKey="capiPageSelector.pinnedToPixel"
                              ns="builder"
                              values={{ id: token.pixelId }}
                              components={{ mono: <span className="font-mono" /> }}
                            />
                          ) : (
                            <span className="italic">
                              {t('capiPageSelector.noPinBroadcast')}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            );
          })()}
          <p className="text-xs text-gray-500 mt-3">
            <Trans
              i18nKey="capiPageSelector.footer"
              ns="builder"
              components={{ strong: <strong /> }}
            />
          </p>

          {/* Optional per-page test code override.
              Hidden in `compact` mode — test event codes are configured
              once per token on the Meta CAPI settings page. */}
          {!compact && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('capiPageSelector.testEvent.label')}{' '}
                <span className="text-gray-500 font-normal">{t('capiPageSelector.testEvent.optional')}</span>
              </label>
              <input
                type="text"
                value={value.testEventCode || ''}
                onChange={(e) => onChange({ ...value, testEventCode: e.target.value })}
                placeholder={t('capiPageSelector.testEvent.placeholder')}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('capiPageSelector.testEvent.hint')}
              </p>
            </div>
          )}
        </div>
      )}

      {!compact && mode === 'custom' && (
        <MetaCapiSettings
          mode="page"
          value={{
            accessToken: value.accessToken,
            testEventCode: value.testEventCode,
            enabled: value.enabled,
          }}
          onChange={(next) =>
            onChange({
              tokenId: undefined,
              accessToken: next.accessToken,
              testEventCode: next.testEventCode,
              enabled: next.enabled,
            })
          }
          hasAccountDefault={hasAccountPool}
          defaultPixelId={defaultPixelId}
        />
      )}

      {/* Per-page enable toggle for inherit / pick modes. Custom mode
          omits this because the embedded MetaCapiSettings already
          renders its own (we'd be doubling the same control). When
          rendered in `compact` mode the wrapping section provides the
          master enable toggle, so suppress it here entirely. */}
      {mode !== 'custom' && !compact && (
        <div className="pt-3 border-t border-gray-700">
          <ToggleSwitch
            label={t('capiPageSelector.pageToggle.label')}
            description={t('capiPageSelector.pageToggle.description')}
            checked={value.enabled !== false}
            onChange={(v) => onChange({ ...value, enabled: v })}
          />
        </div>
      )}
    </div>
  );
};

const ModeButton: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, title, subtitle, active, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={
      'text-left rounded-lg border px-3 py-2.5 transition-colors ' +
      (disabled
        ? 'border-gray-800 bg-gray-900/40 text-gray-600 cursor-not-allowed'
        : active
          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-100'
          : 'border-gray-700 bg-gray-800/60 text-gray-300 hover:border-gray-600')
    }
  >
    <div className="flex items-center gap-2 text-sm font-medium">
      {icon}
      <span>{title}</span>
      {active && <CheckCircle2 className="w-3.5 h-3.5 ml-auto" />}
    </div>
    <div className="text-[11px] mt-1 opacity-80">{subtitle}</div>
  </button>
);
